import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiVisitEngine } from '@/shared/api/client'
import { toaster } from '@/shared/ui'
import { getAxiosErrorData } from '@/shared/utils/axios-error'
import type {
  ILabAnalysesStatsResponse,
  ILabAnalysisResultForm,
  ILabAnalysisResultFormResponse,
  ILabApiError,
  ILabApiResponse,
  ILabOrderActionResponse,
  ILabOrderApproveAllResponse,
  ILabOrderDetailResponse,
  ILabOrderRejectPayload,
  ILabOrderRetestAllPayload,
  ILabOrderRetestAllResponse,
  ILabOrdersByBarcodeResponse,
  ILabOrdersParams,
  ILabOrdersResponse,
  ILabPcrActionPayload,
  ILabPcrActionResponse,
  ILabPcrParams,
  ILabPcrResponse,
} from '../model/lab.type'
import { LAB_BARCODE_ANALYSES_DONE_ERROR_CODE } from '../model/lab.type'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/query-keys'

/**
 * The result-form endpoint has shipped in both spellings — camelCase from the
 * service, snake_case in the API doc — and it now identifies the structure as
 * `analysis_definition_id` instead of the retired `analysis_template_id`. Both
 * are folded onto the camelCase shape the UI reads, so a rename on either side
 * cannot blank out the form.
 */
function normalizeResultForm(raw: any): ILabAnalysisResultForm {
  const parameters: any[] = raw?.parameters ?? []

  return {
    analysisId: raw?.analysisId ?? raw?.analysis_id ?? '',
    analysisDefinitionId:
      raw?.analysisDefinitionId ??
      raw?.analysis_definition_id ??
      // Pre-split payloads called it a template; kept so an un-migrated
      // environment still renders.
      raw?.analysisTemplateId ??
      raw?.analysis_template_id ??
      '',
    analysisCode: raw?.analysisCode ?? raw?.analysis_code ?? '',
    analysisName: raw?.analysisName ?? raw?.analysis_name ?? '',
    status: raw?.status ?? '',
    gender: raw?.gender ?? '',
    resultFormType: raw?.resultFormType ?? raw?.result_form_type ?? '',
    interpretationActive:
      raw?.interpretationActive ?? raw?.interpretation_active ?? false,
    interpretationOptions:
      raw?.interpretationOptions ?? raw?.interpretation_options ?? [],
    emergencyNotificationEligible:
      raw?.emergencyNotificationEligible ??
      raw?.emergency_notification_eligible ??
      false,
    emergencyNotificationInfectionCode:
      raw?.emergencyNotificationInfectionCode ??
      raw?.emergency_notification_infection_code ??
      null,
    parameters: parameters.map((parameter) => ({
      parameterCode:
        parameter?.parameterCode ?? parameter?.parameter_code ?? '',
      name: parameter?.name ?? parameter?.name_ru ?? '',
      resultType: parameter?.resultType ?? parameter?.result_type ?? '',
      unit: parameter?.unit ?? parameter?.default_unit ?? null,
      required: parameter?.required ?? false,
      referenceMin: parameter?.referenceMin ?? parameter?.reference_min ?? null,
      referenceMax: parameter?.referenceMax ?? parameter?.reference_max ?? null,
      cutoffValue: parameter?.cutoffValue ?? parameter?.cutoff_value ?? null,
      enumValues:
        (parameter?.enumValues ?? parameter?.enum_values)?.map(
          (value: any) => ({
            code: value?.code ?? value?.value_code ?? '',
            label:
              value?.label ??
              value?.value_text_ru ??
              value?.code ??
              value?.value_code ??
              '',
            interpretation: value?.interpretation ?? null,
          }),
        ) ?? null,
    })),
  }
}

// ── Raw API ───────────────────────────────────────────────────────────────────

export const labApi = {
  getLabOrders: async (
    params: ILabOrdersParams,
  ): Promise<ILabOrdersResponse> => {
    const query = new URLSearchParams()
    query.set('page', String(params.page))
    query.set('size', String(params.size))
    if (params.search) query.set('search', params.search)
    if (params.donation_type_id)
      query.set('donation_type_id', params.donation_type_id)
    if (params.donor_category_id)
      query.set('donor_category_id', params.donor_category_id)
    if (params.laboratory_id) query.set('laboratory_id', params.laboratory_id)
    if (params.laborant_id) query.set('laborant_id', params.laborant_id)
    if (params.status) query.set('status', params.status)
    if (params.gender) query.set('gender', params.gender)
    if (params.date_from) query.set('date_from', params.date_from)
    if (params.date_to) query.set('date_to', params.date_to)

    const response = await apiVisitEngine.get<ILabOrdersResponse>(
      `/v1/lab/orders?${query.toString()}`,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  getLabOrdersByBarcode: async (
    barcode: string,
  ): Promise<ILabOrdersByBarcodeResponse> => {
    const response = await apiVisitEngine.get<ILabOrdersByBarcodeResponse>(
      `/v1/lab/orders/by-barcode/${encodeURIComponent(barcode)}`,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  getLabOrderById: async (
    orderId: string,
  ): Promise<ILabOrderDetailResponse> => {
    const response = await apiVisitEngine.get<ILabOrderDetailResponse>(
      `/v1/lab/orders/${orderId}`,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  getAnalysesStats: async (): Promise<ILabAnalysesStatsResponse> => {
    const response = await apiVisitEngine.get<ILabAnalysesStatsResponse>(
      `/v1/lab/analyses/stats`,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  getAnalysisResultForm: async (
    analysisId: string,
  ): Promise<ILabAnalysisResultFormResponse> => {
    const response = await apiVisitEngine.get<ILabApiResponse<any>>(
      `/v1/lab/analyses/${analysisId}/result-form`,
      { _skipGlobalErrorHandler: true } as any,
    )
    return {
      ...response.data,
      data: normalizeResultForm(response.data.data),
    }
  },

  acceptLabOrder: async (orderId: string): Promise<ILabOrderActionResponse> => {
    const response = await apiVisitEngine.post<ILabOrderActionResponse>(
      `/v1/lab/orders/${orderId}/accept`,
      undefined,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  rejectLabOrder: async (
    orderId: string,
    payload: ILabOrderRejectPayload,
  ): Promise<ILabOrderActionResponse> => {
    const response = await apiVisitEngine.post<ILabOrderActionResponse>(
      `/v1/lab/orders/${orderId}/reject`,
      payload,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  approveAllLabOrder: async (
    orderId: string,
  ): Promise<ILabOrderApproveAllResponse> => {
    const response = await apiVisitEngine.post<ILabOrderApproveAllResponse>(
      `/v1/lab/orders/${orderId}/approve-all`,
      undefined,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  retestAllLabOrder: async (
    orderId: string,
    payload: ILabOrderRetestAllPayload,
  ): Promise<ILabOrderRetestAllResponse> => {
    const response = await apiVisitEngine.post<ILabOrderRetestAllResponse>(
      `/v1/lab/orders/${orderId}/retest-all`,
      payload,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  getLabPcr: async (params: ILabPcrParams): Promise<ILabPcrResponse> => {
    const query = new URLSearchParams()
    if (params.status) query.set('status', params.status)

    const response = await apiVisitEngine.get<ILabPcrResponse>(
      `/v1/lab/pcr?${query.toString()}`,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  confirmLabPcr: async (
    payload: ILabPcrActionPayload,
  ): Promise<ILabPcrActionResponse> => {
    const response = await apiVisitEngine.post<ILabPcrActionResponse>(
      `/v1/lab/pcr/confirm`,
      payload,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },

  rejectLabPcr: async (
    payload: ILabPcrActionPayload,
  ): Promise<ILabPcrActionResponse> => {
    const response = await apiVisitEngine.post<ILabPcrActionResponse>(
      `/v1/lab/pcr/reject`,
      payload,
      { _skipGlobalErrorHandler: true } as any,
    )
    return response.data
  },
}

/**
 * Toasts the first structured API error, falling back to a given message.
 * Errors whose code is in `silentCodes` are rendered by the caller instead.
 */
function notifyLabApiError(
  error: unknown,
  fallbackMessage: string,
  silentCodes: readonly string[] = [],
) {
  const apiErrors = getAxiosErrorData<{ errors: ILabApiError[] }>(error)?.errors
  if (apiErrors?.length) {
    apiErrors
      .filter((apiError) => !silentCodes.includes(apiError.code))
      .forEach((apiError) => toaster.error({ title: apiError.message }))
    return
  }

  toaster.error({ title: fallbackMessage })
}

/** Same as {@link notifyLabApiError}, wired to a query's error state. */
function useNotifyLabApiError(
  isError: boolean,
  error: unknown,
  fallbackKey: string,
  silentCodes?: readonly string[],
) {
  const { t } = useTranslation()

  useEffect(() => {
    if (!isError) return
    notifyLabApiError(error, t(fallbackKey), silentCodes)
  }, [isError, error, t, fallbackKey, silentCodes])
}

export const useGetLabOrdersQuery = (
  params: ILabOrdersParams,
  { enabled }: { enabled: boolean },
) => {
  const { i18n } = useTranslation()

  const query = useQuery({
    // Response text (fio, laboratory, tube colors, status labels, ...) is
    // localized server-side via Accept-Language, so the language is part of
    // the cache key — switching it must trigger a refetch.
    queryKey: [...queryKeys.lab.orders(params), i18n.language],
    queryFn: () => labApi.getLabOrders(params),
    enabled,
    placeholderData: keepPreviousData,
  })

  useNotifyLabApiError(query.isError, query.error, 'lab.orders.load_error')

  return query
}

// Shown inside the scanner modal, so not toasted on top of it.
const BARCODE_INLINE_ERROR_CODES = [LAB_BARCODE_ANALYSES_DONE_ERROR_CODE]

export const useGetLabOrdersByBarcodeQuery = (
  barcode: string,
  { enabled }: { enabled: boolean },
) => {
  const { i18n } = useTranslation()

  const query = useQuery({
    queryKey: [...queryKeys.lab.byBarcode(barcode), i18n.language],
    queryFn: () => labApi.getLabOrdersByBarcode(barcode),
    enabled: enabled && !!barcode,
  })

  useNotifyLabApiError(
    query.isError,
    query.error,
    'lab.orders.barcode_load_error',
    BARCODE_INLINE_ERROR_CODES,
  )

  return query
}

export const useGetLabOrderByIdQuery = (
  orderId: string,
  { enabled }: { enabled: boolean },
) => {
  const { i18n } = useTranslation()

  const query = useQuery({
    queryKey: [...queryKeys.lab.detail(orderId), i18n.language],
    queryFn: () => labApi.getLabOrderById(orderId),
    enabled: enabled && !!orderId,
  })

  useNotifyLabApiError(
    query.isError,
    query.error,
    'lab.orders.detail_load_error',
  )

  return query
}

export const useGetAnalysesStatsQuery = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const query = useQuery({
    // Plain numbers with no server-rendered text, so the language is not part
    // of the key here.
    queryKey: queryKeys.lab.analysesStats(),
    queryFn: () => labApi.getAnalysesStats(),
    enabled,
    select: (response) => response.data,
  })

  useNotifyLabApiError(query.isError, query.error, 'lab.stats.load_error')

  return query
}

export const useGetAnalysisResultFormQuery = (
  analysisId: string,
  { enabled }: { enabled: boolean },
) => {
  const { i18n } = useTranslation()

  const query = useQuery({
    queryKey: [...queryKeys.lab.analysisResultForm(analysisId), i18n.language],
    queryFn: () => labApi.getAnalysisResultForm(analysisId),
    enabled: enabled && !!analysisId,
  })

  useNotifyLabApiError(
    query.isError,
    query.error,
    'lab.orders.result_form_load_error',
  )

  return query
}

export const useGetLabPcrQuery = (
  params: ILabPcrParams,
  { enabled }: { enabled: boolean },
) => {
  const { i18n } = useTranslation()

  const query = useQuery({
    queryKey: [...queryKeys.lab.pcr(params), i18n.language],
    queryFn: () => labApi.getLabPcr(params),
    enabled,
    placeholderData: keepPreviousData,
  })

  useNotifyLabApiError(query.isError, query.error, 'lab.pcr.load_error')

  return query
}

export const useConfirmLabPcrMutation = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (payload: ILabPcrActionPayload) =>
      labApi.confirmLabPcr(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lab.all() })
    },
    onError: (error) => notifyLabApiError(error, t('lab.pcr.confirm_error')),
  })
}

export const useRejectLabPcrMutation = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (payload: ILabPcrActionPayload) => labApi.rejectLabPcr(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lab.all() })
    },
    onError: (error) => notifyLabApiError(error, t('lab.pcr.reject_error')),
  })
}

export const useAcceptLabOrderMutation = (orderId: string) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: () => labApi.acceptLabOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lab.all() })
    },
    onError: (error) => notifyLabApiError(error, t('lab.orders.accept_error')),
  })
}

export const useRejectLabOrderMutation = (orderId: string) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (payload: ILabOrderRejectPayload) =>
      labApi.rejectLabOrder(orderId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lab.all() })
    },
    onError: (error) => notifyLabApiError(error, t('lab.orders.reject_error')),
  })
}

export const useApproveAllLabOrderMutation = (orderId: string) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: () => labApi.approveAllLabOrder(orderId),
    onSuccess: () => {
      // Approving every analysis changes both the order and its analyses, so
      // refresh the lab caches (orders list, order detail) and analyses caches.
      queryClient.invalidateQueries({ queryKey: queryKeys.lab.all() })
      queryClient.invalidateQueries({ queryKey: queryKeys.analyses.all() })
    },
    onError: (error) =>
      notifyLabApiError(error, t('lab.orders.approve_all_error')),
  })
}

export const useRetestAllLabOrderMutation = (orderId: string) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (payload: ILabOrderRetestAllPayload) =>
      labApi.retestAllLabOrder(orderId, payload),
    onSuccess: () => {
      // Retesting every analysis changes both the order and its analyses, so
      // refresh the lab caches (orders list, order detail) and analyses caches.
      queryClient.invalidateQueries({ queryKey: queryKeys.lab.all() })
      queryClient.invalidateQueries({ queryKey: queryKeys.analyses.all() })
    },
    onError: (error) =>
      notifyLabApiError(error, t('lab.orders.retest_all_error')),
  })
}
